// ONE NAVIGATION MODEL FOR BOTH CLIENTS (founder order, 2026-10-20).
//
// "The tab / settings menu in phone is very different from on website … It should
// always be the same so that customers can easily go from one device to another."
//
// This file is the whole information architecture as DATA: the five bottom-bar
// destinations, the "More" groups with their rows, labels, hints and order, and
// the Dashboard quick actions. It is pure and import-free so it resolves the same
// from either tree, and it is VENDORED BYTE-IDENTICAL into the Expo app as
// `mobile/src/core/nav.ts` by `mobile/scripts/sync-shared.mjs` — so the two clients
// cannot disagree about the map, only about how each one draws it. Each client
// keeps a small key → route/icon table of its own (web `src/components/navTargets.ts`,
// phone `mobile/src/components/navTargets.ts`), because a route and an icon are
// platform facts and a label is not. `mobile/src/__tests__/nav-parity.test.mjs`
// asserts that both clients actually render from this model.
//
// i18n: every `label`, `heading` and `hint` reaches t() as a RUNTIME value, so no
// static `t('…')` scan can see it. The 3+-word strings are still required by the
// web coverage gate's src/lib rule; the one- and two-word labels are not, so a
// NEW label here must be added to the catalogs by hand — the build will not ask.
// Reuse an existing string wherever one fits (the English string IS the key).
//
// Copy rule: describe, never direct. A hint says what is on the page, never what
// the reader should do there.

export type NavKey =
  | 'home'
  | 'portfolio'
  | 'markets'
  | 'progress'
  | 'settings'
  | 'connect'
  | 'budgets'
  | 'household'
  | 'rewards'
  | 'learn'
  | 'robo'
  | 'ai'
  | 'calculators'
  | 'roadmap'
  | 'plans'
  | 'privacy'
  | 'founder';

export interface NavEntry {
  key: NavKey;
  label: string;
  hint: string;
  /** Shown only on the web (on the phone Settings IS the screen holding the list). */
  webOnly?: true;
  /** Shown only to the founder account. */
  founderOnly?: true;
}

export interface NavGroupModel {
  heading: string;
  items: NavEntry[];
}

/** The bottom bar — phone tab bar AND web below 1024px. Same five, same order. */
export const NAV_BAR: { key: NavKey; label: string }[] = [
  { key: 'home', label: 'Home' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'markets', label: 'Markets' },
  { key: 'progress', label: 'Progress' },
  // Labelled "More" (founder 2026-10-31, sim nights 21-24): the tab opens on the
  // More list before any setting, so calling it Settings misnamed it.
  { key: 'settings', label: 'More' },
];

/**
 * "More" — the phone's Settings card, the web Settings card below 1024px, and the
 * web desktop rail's groups. `Settings` is the bar's last tab, so on the web rail
 * it is listed again under Account (webOnly); on the phone the list lives ON the
 * Settings screen and a row pointing at itself would be a dead tap.
 */
export const NAV_MORE: NavGroupModel[] = [
  {
    heading: 'Your money',
    items: [
      { key: 'connect', label: 'Connect', hint: 'Your accounts, in one place' },
      { key: 'budgets', label: 'Budgets', hint: 'Record spending, set monthly limits, see the month against them' },
      { key: 'household', label: 'Household', hint: 'Consent-based family portfolio totals' },
      { key: 'rewards', label: 'Rewards', hint: 'Walk to earn credit against your subscription — health data only if you turn it on' },
    ],
  },
  {
    heading: 'Learn & tools',
    items: [
      { key: 'learn', label: 'Learn', hint: 'Plain-language modules on how markets actually work. No jargon, no tips.' },
      { key: 'robo', label: 'Robo', hint: 'Risk questionnaire and drift against your own targets — no allocation is suggested' },
      { key: 'ai', label: 'Sansara AI', hint: 'Factual Q&A about your own portfolio — describes, never advises' },
      // The public, un-gated /calculators pages. The phone opens the website.
      { key: 'calculators', label: 'Calculators', hint: 'Free calculators on the Sansara website' },
    ],
  },
  {
    // Not "Guidance" on its own: no SEBI RA/RIA registration exists, and this
    // group is the licence-dark machinery. The hint is true of every page behind
    // it without naming one blocker for all of them (they differ). Not "No
    // subscription opens these": the Roadmap page also lists Robo drift, which
    // a plan DOES open, so that hint contradicted the page it labels.
    heading: 'Switch-on path',
    items: [{ key: 'roadmap', label: 'Roadmap', hint: 'What is live, and what is built and waiting' }],
  },
  {
    heading: 'Account',
    items: [
      { key: 'plans', label: 'Plans', hint: 'Compare and manage your plan' },
      { key: 'privacy', label: 'Data & Privacy', hint: 'Where your numbers come from, and everything with access to your data — revoke any time' },
      { key: 'settings', label: 'Settings', hint: 'Your account, your data, your rules.', webOnly: true },
      { key: 'founder', label: 'Founder console', hint: 'All features and their status', founderOnly: true },
    ],
  },
];

/** Dashboard quick actions — the same eight, same order, on both clients. */
export const NAV_QUICK_ACTIONS: NavKey[] = ['connect', 'budgets', 'household', 'rewards', 'learn', 'robo', 'plans', 'privacy'];

/** The More entry for a key (quick actions reuse the row's label). */
export const navEntry = (key: NavKey): NavEntry | undefined => {
  for (const g of NAV_MORE) for (const i of g.items) if (i.key === key) return i;
  return undefined;
};

/**
 * Destinations an under-18 account does not get (server/minor-accounts.mjs
 * refuses every route behind them): broker linking, sharing, step rewards,
 * risk profiling, AI and paid plans. Hiding them is UX; the server is the gate.
 */
export const NAV_HIDDEN_FOR_MINOR: NavKey[] = ['connect', 'household', 'rewards', 'robo', 'ai', 'plans'];

/** Whether a destination is shown — the quick actions and the More list ask the same question. */
export const navShown = (key: NavKey, opts: { minor?: boolean }): boolean => !(opts.minor && NAV_HIDDEN_FOR_MINOR.includes(key));

/**
 * The More groups as one client should draw them. `founder` = may open the
 * console (the owner, or staff with any permission); `staffOnly` = a staff
 * member who is NOT the owner, whose console row reads "Staff console";
 * `minor` = an under-18 account (NAV_HIDDEN_FOR_MINOR).
 */
export const navGroupsFor = (opts: { web: boolean; founder: boolean; staffOnly?: boolean; minor?: boolean }): NavGroupModel[] =>
  NAV_MORE.map((g) => ({
    heading: g.heading,
    items: g.items
      .filter((i) => (opts.web || !i.webOnly) && (opts.founder || !i.founderOnly) && navShown(i.key, opts))
      .map((i) => (i.key === 'founder' && opts.staffOnly ? { ...i, label: 'Staff console', hint: 'Only the parts your role opens' } : i)),
  })).filter((g) => g.items.length > 0);
